"use client"
import { useUserToken } from "@/app/hooks/useUserTracking"
import { hashEmail, hashPhone } from "@/app/utils/security-utils"
import * as Sentry from "@sentry/nextjs"
import { createContext, ReactNode, useCallback, useContext, useMemo } from "react"

interface LeadIdentity {
    email?: string | null
    phone?: string | null
}

interface ITrackingContext {
    track: (...args: any[]) => any
    trackWithEmailHash: (event: string, email: string | null | undefined, data?: Record<string, unknown>) => Promise<void>
    trackLead: (event: string, identity: LeadIdentity, data?: Record<string, unknown>) => Promise<void>
    userToken: string | null
}

const hashOrNull = async (value: string | null | undefined, hash: (v: string) => Promise<string>) => {
    if (!value) return null
    try {
        return await hash(value)
    } catch {
        return null
    }
}

// Define the event structure based on the API requirements
interface TrackingEvent {
    event_name: string
    event_source_url?: string
    event_data?: any
    destination_url?: string
    event_date?: string
}

const TrackingContext = createContext({} as ITrackingContext)

// GTM throws out of dataLayer.push when another script leaves google_tag_manager.zones without its module API,
// so describe that object: its shape is the only way to identify which script wrote it.
const describeTagManager = (): Record<string, unknown> => {
    try {
        const tagManager = window.google_tag_manager
        const zones = tagManager?.zones
        const isObject = zones !== null && typeof zones === "object"

        return {
            gtm_keys: tagManager ? Object.keys(tagManager) : null,
            zones_type: typeof zones,
            zones_constructor: isObject ? (zones.constructor?.name ?? null) : String(zones),
            zones_own_keys: isObject ? Object.getOwnPropertyNames(zones) : null,
            zones_proto_keys: isObject ? Object.getOwnPropertyNames(Object.getPrototypeOf(zones) ?? {}) : null,
        }
    } catch (probeError) {
        return { gtm_probe_error: String(probeError) }
    }
}

const reportDataLayerPushFailure = (error: unknown) => {
    Sentry.captureException(error, {
        tags: { tracking_failure: "datalayer_push" },
        extra: describeTagManager(),
    })
}

export const TrackingProvider = ({ children }: { children: ReactNode }) => {
    const userToken = useUserToken()

    // Function to send event to the Symfony API
    const sendToApi = useCallback(async (eventData: TrackingEvent) => {
        try {
            const apiUrl = `${process.env.NEXT_PUBLIC_RM_BASE_API}/api/events/track`
            const response = await fetch(apiUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(eventData),
            })

            if (!response.ok) {
                console.error("Failed to send event to API:", await response.json())
            }
        } catch (error) {
            console.error("Error sending event to API:", error)
        }
    }, [])

    const track = useCallback(
        (...event: any) => {
            // Ensure we're in a browser environment and dataLayer exists
            if (typeof window !== "undefined") {
                // Initialize dataLayer if it doesn't exist
                if (!window.dataLayer) {
                    window.dataLayer = []
                }

                try {
                    window.dataLayer.push(...event)
                } catch (error) {
                    reportDataLayerPushFailure(error)
                }
            }
            // Check if the event is in the right format for our API
            if (event && event.length > 0 && typeof event[0] === "object") {
                const eventObj = event[0]

                // Map GTM event to our API format
                if (eventObj.event) {
                    const apiEvent: TrackingEvent = {
                        event_name: eventObj.event,
                        event_source_url: window.location.href, // Current page URL as the source
                        event_data: eventObj,
                        event_date: new Date().toISOString(),
                    }

                    // If there's a partner_url in the event, use it as destination_url
                    if (eventObj.partner_url) {
                        apiEvent.destination_url = eventObj.partner_url
                    }

                    // Send to API
                    sendToApi(apiEvent)
                }
            }
        },
        [sendToApi]
    )

    // Push `event` with a SHA-256 `email_hash` when hashing succeeds, otherwise the bare event
    // (mirror of the Vue `triggerEventWithEmailHash`). No raw email is ever pushed.
    const trackWithEmailHash = useCallback(
        async (event: string, email: string | null | undefined, data: Record<string, unknown> = {}) => {
            if (!email) {
                track({ event, ...data })
                return
            }
            try {
                const email_hash = await hashEmail(email)
                track({ event, ...data, email_hash })
            } catch {
                track({ event, ...data })
            }
        },
        [track]
    )

    // Lead AE : le plan exige email_hash ET phone_hash, jamais la valeur en clair.
    const trackLead = useCallback(
        async (event: string, { email, phone }: LeadIdentity, data: Record<string, unknown> = {}) => {
            const [email_hash, phone_hash] = await Promise.all([
                hashOrNull(email, hashEmail),
                hashOrNull(phone, hashPhone),
            ])
            track({
                event,
                ...data,
                ...(email_hash ? { email_hash } : {}),
                ...(phone_hash ? { phone_hash } : {}),
            })
        },
        [track]
    )

    const value = useMemo(
        () => ({ track, trackWithEmailHash, trackLead, userToken }),
        [track, trackWithEmailHash, trackLead, userToken]
    )

    return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>
}

export const useTracking = () => useContext(TrackingContext)
